import { useContext, useMemo, useState, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Form } from 'antd'
import { useTranslation } from 'react-i18next'

import PageWrap from '../../components/layout/PageWrap'
import FormWrap from '../../components/forms/FormWrap'
import FormBlock from '../../components/forms/FormBlock'

import { CreateGiftCard as request } from '../../network/GiftCards'
import { GiftCard, cleanGiftCardPayload } from '../../constants/forms/GiftCard'
import { AppContext } from '../../context/AppContext'
import {
    attachGiftCardPhoneBlurToTemplate,
    promptLinkCustomerByPhone,
    shouldLookupPhone,
} from '../../utils/giftCardPhoneLookup'

const CreateGiftCard = ({ history: historyProp }) => {
    const { user, token } = useContext(AppContext)
    const routerHistory = useHistory()
    const history = historyProp || routerHistory
    const customer = history?.location?.customer
    const [form] = Form.useForm()
    const { t } = useTranslation()
    const [dismissedPhoneLookup, setDismissedPhoneLookup] = useState(null)

    const handlePhoneBlur = useCallback(
        async (formInstance, name, value) => {
            const values = formInstance.getFieldsValue()
            if (
                !shouldLookupPhone(
                    { ...values, phoneNumber: value },
                    dismissedPhoneLookup
                )
            ) {
                return
            }

            await promptLinkCustomerByPhone({
                phone: value,
                token: token.token,
                form: formInstance,
                t,
                dismissedPhone: dismissedPhoneLookup,
                setDismissedPhone: setDismissedPhoneLookup,
            })
        },
        [dismissedPhoneLookup, token.token, t]
    )

    const beforeSubmit = useCallback(
        async (values) => {
            if (shouldLookupPhone(values, dismissedPhoneLookup)) {
                await promptLinkCustomerByPhone({
                    phone: values.phoneNumber,
                    token: token.token,
                    form,
                    t,
                    dismissedPhone: dismissedPhoneLookup,
                    setDismissedPhone: setDismissedPhoneLookup,
                })
            }

            return true
        },
        [dismissedPhoneLookup, token.token, form, t]
    )

    const formTemplate = useMemo(() => {
        let template = GiftCard

        if (customer) {
            const compactPos = {
                amount: { col: 1, row: 3, size: 1 },
                expirationDate: { col: 2, row: 3, size: 1 },
                offeredBy: { col: 3, row: 3, size: 1 },
            }

            template = {
                ...GiftCard,
                sections: GiftCard.sections.map((section) => ({
                    ...section,
                    fields: section.fields
                        .filter((field) => field.key?.[0] !== 'customer')
                        .map((field) => {
                            const key = field.key?.[0]
                            if (compactPos[key]) {
                                return { ...field, pos: compactPos[key] }
                            }
                            return field
                        }),
                })),
            }
        }

        return attachGiftCardPhoneBlurToTemplate(template, handlePhoneBlur)
    }, [customer, handlePhoneBlur])

    const returnToCustomer = () => {
        history.push({
            pathname: `/customers/${customer.id}`,
            defaultTab: 'gift-cards',
        })
    }

    return (
        <PageWrap goBack title="GIFT_CARDS_CREATE">
            <FormWrap
                title="create_gift_card"
                form={form}
                request={request}
                beforeSubmit={beforeSubmit}
                onSuccess={customer ? undefined : '/gift-cards'}
                onCancel={
                    customer ? `/customers/${customer.id}` : '/gift-cards'
                }
                beforeOnSuccess={
                    customer ? returnToCustomer : undefined
                }
                data={{
                    type: 'manual',
                    status: 'validated',
                    grantedByName: user.name,
                    customer: customer?.id
                        ? {
                              value: customer.id,
                              label: customer.name || customer.id,
                          }
                        : undefined,
                }}
                cleanBeforeSending={(oldData) => {
                    oldData.grantedBy = user?.id
                    oldData.type = 'manual'
                    if (customer) {
                        oldData.customer = customer.id
                    }

                    return cleanGiftCardPayload(oldData)
                }}
                invalidate={
                    customer ? ['getCustomerGiftCards'] : ['getGiftCards']
                }
            >
                <FormBlock formTemplate={formTemplate} form={form} />
            </FormWrap>
        </PageWrap>
    )
}

export default CreateGiftCard
